import { useSnackbar } from 'notistack';
import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Slider from 'react-slick';
import { clearErrors, getProductDetails, getSimilarProducts, newReview } from '../../actions/productAction';
import { NextBtn, PreviousBtn } from '../Home/Banner/Banner';
import ProductSlider from '../Home/ProductSlider/ProductSlider';
import Loader from '../Layouts/Loader';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CachedIcon from '@mui/icons-material/Cached';
import FavoriteIcon from '@mui/icons-material/Favorite';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Rating from '@mui/material/Rating';
import TextField from '@mui/material/TextField';
import { NEW_REVIEW_RESET } from '../../constants/productConstants';
import { addItemsToCart } from '../../actions/cartAction';
import { getDeliveryDate, getDiscount } from '../../utils/functions';
import { addToWishlist, removeFromWishlist } from '../../actions/wishlistAction';
import MinCategory from '../Layouts/MinCategory';
import MetaData from '../Layouts/MetaData';
import { useTheme, alpha } from '@mui/material/styles';
import { Box, Button, Chip, Divider, Grid, Paper, Typography, IconButton } from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import './ProductDetails.css';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import axios from 'axios';

const ProductDetails = () => {

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const params = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const [usdToInrRate, setUsdToInrRate] = useState(null);

    // reviews toggle
    const [open, setOpen] = useState(false);
    const [viewAll, setViewAll] = useState(false);
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [isAdding, setIsAdding] = useState(false);
    const [openImageDialog, setOpenImageDialog] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');
    const [zoomed, setZoomed] = useState(false);
    const [transformOrigin, setTransformOrigin] = useState('center center');
    const [quantity, setQuantity] = useState(1);
    const [selectedSize, setSelectedSize] = useState("");

    const [nav1, setNav1] = useState();
    const [nav2, setNav2] = useState();

    const { product, loading, error } = useSelector((state) => state.productDetails);
    const { success, error: reviewError } = useSelector((state) => state.newReview);
    const { cartItems } = useSelector((state) => state.cart);
    const { products: similarProducts, loading: similarProductsLoading } = useSelector((state) => state.similarProducts) || { products: [] };
    const { wishlistItems } = useSelector((state) => state.wishlist);

    const settings = {
        autoplay: true,
        autoplaySpeed: 10000,
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        prevArrow: <PreviousBtn />,
        nextArrow: <NextBtn />,
        pauseOnHover: true,
    };

    const productId = params.id;
    const itemInWishlist = wishlistItems.some((i) => i.product === productId);

    const addToWishlistHandler = () => {
        if (itemInWishlist) {
            dispatch(removeFromWishlist(productId));
            enqueueSnackbar("Remove From Wishlist", { variant: "success" });
        } else {
            dispatch(addToWishlist(productId));
            enqueueSnackbar("Added To Wishlist", { variant: "success" });
        }
    }

    const reviewSubmitHandler = () => {
        if (rating === 0 || !comment.trim()) {
            enqueueSnackbar("Empty Review", { variant: "error" });
            return;
        }
        const formData = new FormData();
        formData.set("rating", rating);
        formData.set("comment", comment);
        formData.set("productId", productId);
        dispatch(newReview(formData));
        setOpen(false);
    }

    const addToCartHandler = () => {
        if (isAdding) return;
        setIsAdding(true);

        if (product.sizes && product.sizes.length > 0 && !selectedSize) {
            enqueueSnackbar("Please select a size", { variant: "warning" });
            setIsAdding(false);
            return;
        }

        const existingItem = cartItems.find(i => i.product === productId && i.size === selectedSize);
        const newQuantity = existingItem ? existingItem.quantity + quantity : quantity;

        if (newQuantity > product.stock) {
            enqueueSnackbar("Maximum Order Quantity Exceeded", { variant: "warning" });
            setIsAdding(false);
            return;
        }

        dispatch(addItemsToCart(productId, newQuantity, selectedSize));
        enqueueSnackbar("Product Added To Cart", { variant: "success" });
        setTimeout(() => {
            setIsAdding(false);
        }, 1500);
    }

    const handleDialogClose = () => {
        setOpen(!open);
    }

    const handleImageClick = (image) => {
        setSelectedImage(image);
        setOpenImageDialog(true);
    };

    const handleImageDialogClose = () => {
        setOpenImageDialog(false);
        setZoomed(false);
    };

    const handleZoomedImageClick = (e) => {
        if (zoomed) {
            setZoomed(false);
        } else {
            const { left, top, width, height } = e.target.getBoundingClientRect();
            const x = ((e.clientX - left) / width) * 100;
            const y = ((e.clientY - top) / height) * 100;
            setTransformOrigin(`${x}% ${y}%`);
            setZoomed(true);
        }
    };

    const itemInCart = cartItems.some((i) => i.product === productId);

    const goToCart = () => {
        navigate('/cart');
    }

    const buyNow = () => {
        addToCartHandler();
        navigate('/shipping');
    }

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        if (reviewError) {
            enqueueSnackbar(reviewError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (success) {
            enqueueSnackbar("Review Submitted Successfully", { variant: "success" });
            dispatch({ type: NEW_REVIEW_RESET });
        }
        dispatch(getProductDetails(productId));

        const recentlyViewed = JSON.parse(localStorage.getItem('recentlyViewed')) || [];
        const updatedRecentlyViewed = [productId, ...recentlyViewed.filter(id => id !== productId)].slice(0, 10);
        localStorage.setItem('recentlyViewed', JSON.stringify(updatedRecentlyViewed));

        axios.get('https://api.exchangerate-api.com/v4/latest/USD')
            .then(response => {
                setUsdToInrRate(response.data.rates.INR);
            })
            .catch(error => {
                console.error("Error fetching exchange rate", error);
                setUsdToInrRate(88.79); // fallback rate
            });

        // eslint-disable-next-line
    }, [dispatch, productId, error, reviewError, success, enqueueSnackbar]);

        useEffect(() => {
        if (product && product.category) {
            dispatch(getSimilarProducts(product.category.name));
        }
    }, [dispatch, product]);

    return (
        <>
            {loading ? <Loader /> : (
                <>
                    {product && <MetaData title={product.name} description={product.description} image={product.images && product.images[0].highUrl} url={window.location.href} />}
                    <MinCategory />
                    <Box sx={{ mt: { xs: '12', sm: '0' }, p: 2, bgcolor: 'background.default', color: 'text.primary' }}>
                        <Grid container spacing={4}>
                            {/* Image Column */}
                            <Grid item xs={12} md={5}>
                                <Paper elevation={3} sx={{ p: 2, position: 'sticky', top: '6rem', '.slick-dots li.slick-active button:before': { color: theme.palette.primary.main }, '.slick-dots li button:before': { color: theme.palette.text.secondary }}}>
                                    <Slider {...settings} asNavFor={nav2} ref={setNav1}>
                                        {product && product.images && product.images.map((item, i) => (
                                            <img draggable="false" style={{ width: '100%', height: '30rem', objectFit: 'contain', cursor: 'zoom-in' }} src={item.highUrl} alt={product.name} key={i} onClick={() => handleImageClick(item.highUrl)} />
                                        ))}
                                    </Slider>
                                    <Box sx={{ position: 'absolute', bottom: 16, right: 16 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Images are for illustration purpose only
                                        </Typography>
                                    </Box>
                                    <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                                        <IconButton onClick={addToWishlistHandler} sx={{
                                            color: itemInWishlist ? theme.palette.error.main : 'inherit',
                                            transition: 'transform 0.3s ease-in-out',
                                            '&:hover': { transform: 'scale(1.1)' }
                                        }}>
                                            <FavoriteIcon />
                                        </IconButton>
                                    </Box>
                                </Paper>
                                {/* Moved thumbnail slider outside the Paper component */}
                                <Slider
                                    asNavFor={nav1}
                                    ref={setNav2}
                                    slidesToShow={4}
                                    swipeToSlide={true}
                                    focusOnSelect={true}
                                    infinite={false}
                                    arrows={false}
                                    className="thumbnail-slider"
                                    responsive={[
                                        {
                                            breakpoint: 900,
                                            settings: {
                                                slidesToShow: 3,
                                            }
                                        },
                                        {
                                            breakpoint: 600,
                                            settings: {
                                                slidesToShow: 2,
                                            }
                                        },
                                        {
                                            breakpoint: 300,
                                            settings: {
                                                slidesToShow: 1,
                                            }
                                        }
                                    ]}
                                >
                                    {product && product.images && product.images.map((item, i) => (
                                        <Paper key={i} elevation={1} sx={{ width: '3rem', height: '9rem', p: 0.5, cursor: 'pointer', position: 'relative', overflow: 'hidden', '&:hover': { borderColor: 'primary.main' } }} className="thumbnail-item">
                                            <img draggable="false" src={item.lowUrl} alt={product.name} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                        </Paper>
                                    ))}
                                </Slider>
                            </Grid>

                            {/* Details Column */}
                            <Grid item xs={12} md={4}>
                                <Box>
                                    <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                        {product && product.name}
                                    </Typography>
                                    {product.brand && (
                                        <Link to={`/brand/${product.brand._id}`} style={{ textDecoration: 'none', color: theme.palette.primary.main }}>
                                            <Typography variant="subtitle1" gutterBottom>
                                                Visit the {product.brand.name} Store
                                            </Typography>
                                        </Link>
                                    )}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <Rating name="read-only" value={product && product.ratings} readOnly size="small" precision={0.5} />
                                        <Typography variant="body2" color="text.secondary">({product && product.numOfReviews} ratings)</Typography>
                                    </Box>
                                    <Divider sx={{ my: 2 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                                        {product && <Chip label={`-${getDiscount(product.price, product.mrp)}%`} color="error" />}
                                        <Typography variant="h4">
                                            <sup>₹</sup>{product && product.price?.toLocaleString()}
                                            {usdToInrRate && (
                                                <>
                                                    {' / '}
                                                    <Typography component="sup" variant="h6" sx={{display: 'inline-block'}}>$</Typography>
                                                    <Typography component="span" variant="h4" sx={{display: 'inline-block'}}>{((product.price / usdToInrRate) + 10).toFixed(2)}</Typography>
                                                </>
                                            )}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                        M.R.P.: ₹{product && product.mrp?.toLocaleString()}
                                        {usdToInrRate && (
                                            <Typography component="span" variant="body2" sx={{ ml: 1, color: theme.palette.text.secondary }}>
                                                / ${((product.mrp / usdToInrRate) + 10).toFixed(2)}
                                            </Typography>
                                        )}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Inclusive of all taxes
                                    </Typography>

                                    <Box sx={{ mt: 3 }}>
                                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Offers</Typography>
                                        {Array(3).fill("").map((el, i) => (
                                            <Paper key={i} elevation={1} sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'background.paper' }}>
                                                <LocalOfferIcon color="success" />
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Bank Offer</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        15% Instant discount on first order of 500 and above <Link to="/" style={{ textDecoration: 'none', color: theme.palette.primary.main }}>T&C</Link>
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        ))}
                                    </Box>

                                    <Divider sx={{ my: 3 }} />

                                    <Grid container spacing={2} textAlign="center">
                                        <Grid item xs={6}>
                                            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1}}>
                                                <CachedIcon color="primary" />
                                                <Typography variant="body2" color="primary.main">Unlimited Replacement</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1}}>
                                                <VerifiedUserIcon color="primary" />
                                                <Typography variant="body2" color="primary.main">{product && product.warranty} Year Warranty</Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    <Box sx={{ mt: 3 }}>
                                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>About this item</Typography>
                                        <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                                            {product && product.description}
                                        </Typography>
                                    </Box>

                                    {product && product.highlights && product.highlights.length > 0 && (
                                        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {product.highlights.map((highlight, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                                                        color: theme.palette.text.primary,
                                                        borderRadius: '4px',
                                                        padding: '2px 6px',
                                                        display: 'inline-block',
                                                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        {highlight}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    {product && product.sizes && product.sizes.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Size</Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {product.sizes.map((size, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={size}
                                                        onClick={() => setSelectedSize(size)}
                                                        variant={selectedSize === size ? "filled" : "outlined"}
                                                        color="primary"
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Grid>

                            {/* Action Column */}
                            <Grid item xs={12} md={3}>
                                <Paper elevation={3} sx={{ p: 3, position: 'sticky', top: '6rem' }}>
                                    <Typography variant="h4" sx={{ mb: 1 }}>
                                        <sup>₹</sup>{product && product.price?.toLocaleString()}
                                        {usdToInrRate && (
                                            <>
                                                {' / '}
                                                <Typography component="sup" variant="h6" sx={{display: 'inline-block'}}>$</Typography>
                                                <Typography component="span" variant="h4" sx={{display: 'inline-block'}}>{(product.price / usdToInrRate + 10).toFixed(2)}</Typography>
                                            </>
                                        )}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Delivery by <span style={{ color: theme.palette.success.main, fontWeight: 'bold' }}>{getDeliveryDate()}</span>
                                    </Typography>
                                    <Typography variant="h6" sx={{ color: product && product.stock > 0 ? theme.palette.success.main : theme.palette.error.main, my: 1 }}>
                                        {product && product.stock > 0 ? "In Stock" : "Out of Stock"}
                                    </Typography>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, mb: 2 }}>
                                        <Typography>Quantity:</Typography>
                                        <Button variant="outlined" size="small" onClick={() => setQuantity(q => q > 1 ? q - 1 : 1)}>-</Button>
                                        <Typography>{quantity}</Typography>
                                        <Button variant="outlined" size="small" onClick={() => setQuantity(q => q < product.stock ? q + 1 : product.stock)}>+</Button>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                                        {product && product.stock > 0 && (
                                            <Button
                                                variant="contained"
                                                className={`cart-button ${isAdding ? 'clicked' : ''}`}
                                                onClick={itemInCart ? goToCart : addToCartHandler}
                                                sx={{ 
                                                    transition: 'transform 0.2s', 
                                                    '&:hover': { transform: 'scale(1.05)' },
                                                    backgroundColor: '#f0c14b',
                                                    color: '#111',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                <span className="add-to-cart-text">
                                                    <ShoppingCartIcon sx={{mr: 1}}/>
                                                    {itemInCart ? "Go to Cart" : "Add to Cart"}
                                                </span>
                                                <span className="added-text">Added</span>
                                                <ShoppingCartIcon className="cart-icon-anim" />
                                                <Inventory2Icon className="box-icon" />
                                            </Button>
                                        )}
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            startIcon={<FlashOnIcon />}
                                            onClick={buyNow}
                                            disabled={product && product.stock < 1}
                                            sx={{ transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
                                        >
                                            {product && product.stock < 1 ? "Out of Stock" : "Buy Now"}
                                        </Button>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                                        <VerifiedUserIcon fontSize="small" color="action" />
                                        <Typography variant="body2" color="text.secondary">Secure transaction</Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                                        <Typography variant="body2" color="text.secondary">Sold by</Typography>
                                            <Typography style={{ textDecoration: 'none', color: theme.palette.primary.main }}>
                                                AmritLok
                                            </Typography>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Product Description & Reviews */}
                        <Grid container spacing={4} sx={{ mt: 4 }}>

                            <Grid item xs={12}>
                                <Paper elevation={2} sx={{ p: 3, bgcolor: 'background.paper' }}>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>Specifications</Typography>
                                    <Divider />
                                    <Grid container spacing={4} sx={{ mt: 1 }}>
                                        <Grid item xs={12} md={6}>
                                            {product && product.specifications?.slice(0, Math.ceil((product.specifications?.length || 0) / 2)).map((spec, i) => (
                                                <Grid container key={i} sx={{ my: 1 }}>
                                                    <Grid item xs={5}><Typography color="text.secondary">{spec.title}</Typography></Grid>
                                                    <Grid item xs={7}><Typography>{spec.description}</Typography></Grid>
                                                </Grid>
                                            ))}
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            {product && product.specifications?.slice(Math.ceil((product.specifications?.length || 0) / 2)).map((spec, i) => (
                                                <Grid container key={i} sx={{ my: 1 }}>
                                                    <Grid item xs={5}><Typography color="text.secondary">{spec.title}</Typography></Grid>
                                                    <Grid item xs={7}><Typography>{spec.description}</Typography></Grid>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>

                            <Grid item xs={12}>
                                <Paper elevation={2} sx={{ p: 3, bgcolor: 'background.paper' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Customer reviews</Typography>
                                        <Button variant="outlined" onClick={handleDialogClose}>Write a review</Button>
                                    </Box>
                                    <Divider />
                                    <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                                        <Box sx={{ width: '25%' }}>
                                            <Rating name="read-only" value={product && product.ratings} readOnly size="large" precision={0.5} />
                                            <Typography variant="h6" color="text.secondary">{product && product.ratings?.toFixed(1)} out of 5</Typography>
                                        </Box>
                                        <Box sx={{ width: '75%' }}>
                                            {/* Placeholder for review distribution graph */}
                                        </Box>
                                    </Box>
                                    <Divider sx={{ my: 2 }} />
                                    <Box sx={{ my: 2 }}>
                                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Reviews with images</Typography>
                                        {/* Placeholder for reviews with images */}
                                    </Box>
                                    <Divider sx={{ my: 2 }} />
                                    <Box>
                                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Top reviews from India</Typography>
                                        {product && viewAll ?
                                            product.reviews?.map((rev, i) => (
                                                <Box key={i} sx={{ my: 2, p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                        <img src={rev.user.avatar.url} alt={rev.name} style={{ width: 32, height: 32, borderRadius: '50%' }} />
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{rev.name}</Typography>
                                                    </Box>
                                                    <Rating name="read-only" value={rev.rating} readOnly size="small" precision={0.5} />
                                                    <Typography variant="body2" color="text.secondary">{rev.comment}</Typography>
                                                </Box>
                                            )).reverse()
                                            :
                                            product && product.reviews?.slice(-3).map((rev, i) => (
                                                <Box key={i} sx={{ my: 2, p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                        <img src={rev.user.avatar.url} alt={rev.name} style={{ width: 32, height: 32, borderRadius: '50%' }} />
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{rev.name}</Typography>
                                                    </Box>
                                                    <Rating name="read-only" value={rev.rating} readOnly size="small" precision={0.5} />
                                                    <Typography variant="body2" color="text.secondary">{rev.comment}</Typography>
                                                </Box>
                                            )).reverse()
                                        }
                                        {product && product.reviews?.length > 3 &&
                                            <Button fullWidth onClick={() => setViewAll(!viewAll)} sx={{ mt: 2 }}>{viewAll ? "See less reviews" : "See all reviews"}</Button>
                                        }
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Sliders */}
                        <Box sx={{ mt: 4 }}>
                            <ProductSlider title={"Similar Products"} tagline={"Based on the category"} products={similarProducts} loading={similarProductsLoading} />
                        </Box>

                    </Box>

                    <Dialog open={open} onClose={handleDialogClose}>
                        <DialogTitle>Submit Review</DialogTitle>
                        <DialogContent>
                            <Rating
                                onChange={(e) => setRating(e.target.value)}
                                value={rating}
                                size="large"
                            />
                            <TextField
                                autoFocus
                                margin="dense"
                                id="comment"
                                label="Comment"
                                type="text"
                                fullWidth
                                multiline
                                rows={4}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleDialogClose}>Cancel</Button>
                            <Button onClick={reviewSubmitHandler}>Submit</Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openImageDialog}
                        onClose={handleImageDialogClose}
                        maxWidth="lg"
                        fullWidth
                    >
                        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                            <img
                                src={selectedImage}
                                alt="zoomed"
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    transform: zoomed ? 'scale(2)' : 'scale(1)',
                                    transformOrigin: transformOrigin,
                                    transition: 'transform 0.3s ease-in-out',
                                    cursor: 'zoom-' + (zoomed ? 'out' : 'in'),
                                }}
                                onClick={handleZoomedImageClick}
                            />
                        </DialogContent>
                    </Dialog>
                </>
            )}
        </>
    );
};

export default ProductDetails;